import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Check, CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { type QuizDetail } from "~/api/types";
import { completeQuiz } from "~/api/v2/requests";
import { QuizReason } from "~/molecules/QuizReason";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Item, ItemActions, ItemContent, ItemTitle } from "~/atoms/item";
import { Progress } from "~/atoms/progress";
import { cn } from "~/lib/utils";

interface Props {
  quiz: QuizDetail;
  onCompleted: () => Promise<unknown>;
}

/**
 * Take a quiz question by question. The taker must select the correct answer to
 * advance; the answer options carry the correct flag, so correctness is checked
 * locally. Finishing the last question marks the quiz completed for the user.
 */
const QuizTaker = ({ quiz, onCompleted }: Props) => {
  const { t } = useTranslation();

  const [questionIndex, setQuestionIndex] = useState(0);
  const [answeredCorrectly, setAnsweredCorrectly] = useState(false);
  const [wrongTried, setWrongTried] = useState<Set<number>>(new Set());
  const [isFinishing, setIsFinishing] = useState(false);

  const total = quiz.questions.length;
  const question = quiz.questions[questionIndex];
  const isLast = questionIndex === total - 1;

  if (!question) return null;

  const selectOption = (index: number) => {
    if (answeredCorrectly || wrongTried.has(index)) return;
    if (question.options[index]?.isCorrect) {
      setAnsweredCorrectly(true);
    } else {
      setWrongTried((prev) => new Set(prev).add(index));
    }
  };

  const goNext = () => {
    setQuestionIndex((index) => index + 1);
    setAnsweredCorrectly(false);
    setWrongTried(new Set());
  };

  const finish = async () => {
    setIsFinishing(true);
    try {
      await completeQuiz(quiz.id);
      await onCompleted();
    } catch {
      toast.error(t(($) => $.anchoring.quiz.take.error));
    } finally {
      setIsFinishing(false);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="flex w-full max-w-2xl flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-sm">
            {t(($) => $.anchoring.quiz.take.progress, {
              current: questionIndex + 1,
              total,
            })}
          </p>
          <Progress value={(questionIndex / total) * 100} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{question.question}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {question.options.map((option, index) => {
                const isWrong = wrongTried.has(index);
                const isCorrect = answeredCorrectly && option.isCorrect;
                return (
                  <Item
                    key={index}
                    variant="outline"
                    asChild
                    className={cn(
                      "enabled:hover:bg-muted cursor-pointer text-left disabled:cursor-not-allowed",
                      isCorrect && "border-success bg-success/10 text-success",
                      isWrong &&
                        "border-destructive bg-destructive/10 text-destructive",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => selectOption(index)}
                      disabled={answeredCorrectly || isWrong}
                    >
                      <ItemContent>
                        <ItemTitle className="line-clamp-none">
                          {option.text}
                        </ItemTitle>
                      </ItemContent>
                      {(isCorrect || isWrong) && (
                        <ItemActions>
                          {isCorrect ? (
                            <Check className="size-4" />
                          ) : (
                            <X className="size-4" />
                          )}
                        </ItemActions>
                      )}
                    </button>
                  </Item>
                );
              })}
            </div>

            {answeredCorrectly && question.reason && (
              <Alert variant="success">
                <CheckCircle2 />
                <AlertTitle>
                  {t(($) => $.anchoring.quiz.take.reasonHeading)}
                </AlertTitle>
                <AlertDescription>
                  <QuizReason
                    html={question.reason}
                    className="text-foreground"
                  />
                </AlertDescription>
              </Alert>
            )}

            {wrongTried.size > 0 && !answeredCorrectly && (
              <Alert variant="destructive">
                <XCircle />
                <AlertTitle>
                  {t(($) => $.anchoring.quiz.take.incorrect)}
                </AlertTitle>
              </Alert>
            )}
          </CardContent>
          <CardFooter>
            {isLast ? (
              <Button
                onClick={() => void finish()}
                disabled={!answeredCorrectly || isFinishing}
              >
                {isFinishing && (
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                )}
                {t(($) => $.anchoring.quiz.take.finish)}
              </Button>
            ) : (
              <Button onClick={goNext} disabled={!answeredCorrectly}>
                {t(($) => $.anchoring.quiz.take.next)}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default QuizTaker;
